// Per-subscriber view: profile, edit form, activity timeline,
// status toggle, and delete. Shown at /subscribers/:id.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  ChevronDown,
  Send,
  Mail,
  MailOpen,
  MousePointerClick,
  CheckCircle2,
  XCircle,
  UserPlus,
  UserMinus,
  Pencil,
  Trash2,
  Flag,
  type LucideIcon,
} from 'lucide-react';
import { subscribersApi } from '../lib/api';
import { ApiError } from '../lib/api';
import type {
  SubscriberDetail,
  SubscriberEvent,
  SubscriberStatus,
} from '@shared/types';

const STATUS_LABELS: Record<SubscriberStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  blocked: 'Blocked',
};

const EVENT_LABELS: Record<string, string> = {
  subscribed: 'Subscribed',
  unsubscribed: 'Unsubscribed',
  welcome_scheduled: 'Welcome email scheduled',
  welcome_sent: 'Welcome email sent',
  newsletter_sent: 'Newsletter sent',
  referred_signup: 'Referral signup',
  signup_blocked: 'Signup blocked',
  admin_updated: 'Admin updated',
  admin_deleted: 'Admin deleted',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  complained: 'Spam complaint',
  failed: 'Failed',
};

const EVENT_VISUAL: Record<
  string,
  { icon: LucideIcon; color: string; bg: string }
> = {
  subscribed:      { icon: UserPlus,          color: 'text-green-700',  bg: 'bg-green-100'  },
  unsubscribed:    { icon: UserMinus,         color: 'text-slate-600',  bg: 'bg-slate-100'  },
  welcome_scheduled: { icon: Mail,            color: 'text-slate-500',  bg: 'bg-slate-100'  },
  welcome_sent:    { icon: Mail,              color: 'text-blue-700',   bg: 'bg-blue-100'   },
  newsletter_sent: { icon: Send,              color: 'text-blue-700',   bg: 'bg-blue-100'   },
  referred_signup: { icon: UserPlus,          color: 'text-green-700',  bg: 'bg-green-100'  },
  signup_blocked:  { icon: AlertTriangle,     color: 'text-red-700',    bg: 'bg-red-100'    },
  admin_updated:   { icon: Pencil,            color: 'text-amber-700',  bg: 'bg-amber-100'  },
  admin_deleted:   { icon: Trash2,            color: 'text-red-700',    bg: 'bg-red-100'    },
  sent:            { icon: Send,              color: 'text-slate-600',  bg: 'bg-slate-100'  },
  delivered:       { icon: CheckCircle2,      color: 'text-green-700',  bg: 'bg-green-100'  },
  opened:          { icon: MailOpen,          color: 'text-indigo-700', bg: 'bg-indigo-100' },
  clicked:         { icon: MousePointerClick, color: 'text-purple-700', bg: 'bg-purple-100' },
  bounced:         { icon: AlertTriangle,     color: 'text-red-700',    bg: 'bg-red-100'    },
  complained:      { icon: Flag,              color: 'text-red-700',    bg: 'bg-red-100'    },
  failed:          { icon: XCircle,           color: 'text-red-700',    bg: 'bg-red-100'    },
};

const METADATA_LABELS: Record<string, string> = {
  resend_email_id: 'Resend email ID',
  url: 'URL',
  subject: 'Subject',
  send_id: 'Send ID',
  recipient_id: 'Recipient ID',
  user_agent: 'User agent',
  ip: 'IP address',
  reason: 'Reason',
  bounce_type: 'Bounce type',
  email: 'Email',
  subscriber_id: 'Subscriber ID',
  source: 'Source',
  source_slug: 'Source slug',
  referred_by: 'Referred by',
  rule_id: 'Rule ID',
  rule_type: 'Rule type',
  pattern: 'Pattern',
};

function formatEventTimestamp(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function SubscriberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [subscriber, setSubscriber] = useState<SubscriberDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [status, setStatus] = useState<SubscriberStatus>('confirmed');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const [events, setEvents] = useState<SubscriberEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsLoadingMore, setEventsLoadingMore] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr(null);
    try {
      const { subscriber } = await subscribersApi.get(id);
      setSubscriber(subscriber);
      setFirstName(subscriber.first_name ?? '');
      setLastName(subscriber.last_name ?? '');
      setStatus(subscriber.status);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load subscriber');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const EVENTS_PAGE_SIZE = 50;

  const refreshEvents = useCallback(async () => {
    if (!id) return;
    setEventsLoading(true);
    try {
      const { events, total } = await subscribersApi.listEvents(id, {
        page: 1,
        page_size: EVENTS_PAGE_SIZE,
      });
      setEvents(events);
      setEventsTotal(total);
      setEventsPage(1);
    } catch {
      setEvents([]);
      setEventsTotal(0);
      setEventsPage(1);
    } finally {
      setEventsLoading(false);
    }
  }, [id]);

  const loadMoreEvents = useCallback(async () => {
    if (!id || eventsLoadingMore) return;
    setEventsLoadingMore(true);
    try {
      const next = eventsPage + 1;
      const { events: more, total } = await subscribersApi.listEvents(id, {
        page: next,
        page_size: EVENTS_PAGE_SIZE,
      });
      setEvents((prev) => [...prev, ...more]);
      setEventsTotal(total);
      setEventsPage(next);
    } finally {
      setEventsLoadingMore(false);
    }
  }, [id, eventsPage, eventsLoadingMore]);

  useEffect(() => {
    void refresh();
    void refreshEvents();
  }, [refresh, refreshEvents]);

  async function save() {
    if (!subscriber) return;
    setSaving(true);
    setSaveErr(null);
    try {
      await subscribersApi.update(subscriber.id, {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        status,
        expected_updated_at: subscriber.updated_at,
      });
      setEditing(false);
      await refresh();
      await refreshEvents();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict') {
        setSaveErr('Someone else edited this subscriber. Reload to see the latest.');
      } else {
        setSaveErr(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  }

  async function quickStatusToggle(next: SubscriberStatus) {
    if (!subscriber) return;
    setSaving(true);
    setSaveErr(null);
    try {
      await subscribersApi.update(subscriber.id, {
        status: next,
        expected_updated_at: subscriber.updated_at,
      });
      await refresh();
      await refreshEvents();
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Status change failed');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!subscriber) return;
    setDeleting(true);
    try {
      await subscribersApi.remove(subscriber.id);
      navigate('/subscribers');
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        Loading subscriber…
      </div>
    );
  }
  if (err || !subscriber) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
        <p>Could not load this subscriber.</p>
        {err && <p className="text-red-600 text-xs">{err}</p>}
        <Link to="/subscribers" className="text-blue-700 hover:underline">
          Back to subscribers
        </Link>
      </div>
    );
  }

  const fullName = [subscriber.first_name ?? '', subscriber.last_name ?? '']
    .join(' ')
    .trim();

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <Link
          to="/subscribers"
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> All subscribers
        </Link>

        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl text-slate-900 break-words">
              {fullName || subscriber.email}
            </h1>
            {fullName && (
              <p className="text-sm text-slate-600 mt-0.5 break-all">{subscriber.email}</p>
            )}
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded ${
                  subscriber.status === 'confirmed'
                    ? 'bg-green-100 text-green-800'
                    : subscriber.status === 'unsubscribed'
                      ? 'bg-slate-200 text-slate-700'
                      : subscriber.status === 'bounced' ||
                          subscriber.status === 'blocked'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                }`}
              >
                {STATUS_LABELS[subscriber.status]}
              </span>
              <span className="text-slate-500">
                {subscriber.days_subscribed} day
                {subscriber.days_subscribed === 1 ? '' : 's'} subscribed
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {subscriber.status === 'confirmed' ? (
              <button
                onClick={() => quickStatusToggle('unsubscribed')}
                disabled={saving}
                className="btn-secondary"
              >
                Mark unsubscribed
              </button>
            ) : (
              <button
                onClick={() => quickStatusToggle('confirmed')}
                disabled={saving}
                className="btn-secondary"
              >
                Mark subscribed
              </button>
            )}
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="btn-primary"
              >
                Edit
              </button>
            )}
          </div>
        </header>

        {saveErr && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> {saveErr}
          </p>
        )}

        {/* Profile / Edit */}
        <section className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 mb-6">
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  First name
                </label>
                <input
                  className="input w-full"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Last name
                </label>
                <input
                  className="input w-full"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as SubscriberStatus)}
                  className="input"
                >
                  {(Object.keys(STATUS_LABELS) as SubscriberStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2 pt-2">
                <button
                  onClick={save}
                  disabled={saving}
                  className="btn-primary text-sm"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFirstName(subscriber.first_name ?? '');
                    setLastName(subscriber.last_name ?? '');
                    setStatus(subscriber.status);
                  }}
                  disabled={saving}
                  className="btn-ghost text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field label="Source">
                <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
                  {subscriber.source_slug}
                </code>
              </Field>
              <Field label="Signed up">
                {new Date(subscriber.created_at).toLocaleString()}
              </Field>
              {subscriber.confirmed_at && (
                <Field label="Confirmed">
                  {new Date(subscriber.confirmed_at).toLocaleString()}
                </Field>
              )}
              {subscriber.unsubscribed_at && (
                <Field label="Unsubscribed">
                  {new Date(subscriber.unsubscribed_at).toLocaleString()}
                </Field>
              )}
              <Field label="Emails received">{subscriber.total_emails_received}</Field>
              <Field label="Opened">{subscriber.total_opened}</Field>
              <Field label="Clicked">{subscriber.total_clicked}</Field>
              <Field label="Bounced">{subscriber.total_bounced}</Field>
              <Field label="Referral code">
                <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
                  {subscriber.referral_code ?? '—'}
                </code>
              </Field>
              <Field label="Referrals sent">{subscriber.referral_count}</Field>
              {subscriber.referred_by && (
                <Field label="Referred by">
                  <Link
                    to={`/subscribers/${subscriber.referred_by.id}`}
                    className="text-blue-700 hover:underline"
                  >
                    {subscriber.referred_by.email}
                  </Link>
                </Field>
              )}
            </dl>
          )}
        </section>

        {/* Activity */}
        <section className="bg-white border border-slate-200 rounded-md mb-6">
          <header className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between">
            <h2 className="text-sm font-medium text-slate-900">Activity</h2>
            {!eventsLoading && eventsTotal > 0 && (
              <span className="text-xs text-slate-500">
                {events.length === eventsTotal
                  ? `${eventsTotal} event${eventsTotal === 1 ? '' : 's'}`
                  : `${events.length} of ${eventsTotal} events`}
              </span>
            )}
          </header>
          {eventsLoading ? (
            <p className="p-5 text-sm text-slate-400">Loading…</p>
          ) : events.length === 0 ? (
            <p className="p-5 text-sm text-slate-400">No events yet.</p>
          ) : (
            <>
              <ul className="divide-y divide-slate-100">
                {groupEvents(events).map((g) =>
                  g.kind === 'group' ? (
                    <ActivityGroupRow key={g.key} group={g} />
                  ) : (
                    <ActivityRow key={g.event.id} event={g.event} />
                  ),
                )}
              </ul>
              {events.length < eventsTotal && (
                <div className="border-t border-slate-100 px-4 sm:px-5 py-3 text-center">
                  <button
                    onClick={loadMoreEvents}
                    disabled={eventsLoadingMore}
                    className="text-sm text-blue-700 hover:text-blue-900 disabled:opacity-50"
                  >
                    {eventsLoadingMore
                      ? 'Loading…'
                      : `Load ${Math.min(EVENTS_PAGE_SIZE, eventsTotal - events.length)} more`}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Danger zone */}
        <section className="border border-red-200 bg-red-50 rounded-md p-4 sm:p-5">
          <h2 className="text-sm font-medium text-red-900 mb-2">Danger zone</h2>
          {showDelete ? (
            <div className="text-sm">
              <p className="text-red-900 mb-2">
                This permanently removes the subscriber and all their activity. Type
                {' '}<code className="bg-white px-1 rounded">DELETE</code>{' '}to confirm.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  className="input"
                  value={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.value)}
                  placeholder="DELETE"
                />
                <button
                  onClick={doDelete}
                  disabled={confirmDelete !== 'DELETE' || deleting}
                  className="btn-primary !bg-red-600 hover:!bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete subscriber'}
                </button>
                <button
                  onClick={() => {
                    setShowDelete(false);
                    setConfirmDelete('');
                  }}
                  disabled={deleting}
                  className="btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowDelete(true)}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Delete this subscriber
            </button>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">
        {label}
      </dt>
      <dd className="text-slate-900">{children}</dd>
    </div>
  );
}

type EventGroup =
  | { kind: 'single'; event: SubscriberEvent }
  | {
      kind: 'group';
      key: string;
      sendId: string;
      subject: string | null;
      events: SubscriberEvent[];
    };

// Collapse runs of consecutive events that share the same send_id
// into one group. Events are already DESC-ordered, so consecutive
// same-send rows are the typical newsletter sent→delivered→opened
// →clicked sequence we want to fold together.
function groupEvents(events: SubscriberEvent[]): EventGroup[] {
  const out: EventGroup[] = [];
  let i = 0;
  while (i < events.length) {
    const e = events[i];
    if (e.send_id) {
      let j = i + 1;
      while (j < events.length && events[j].send_id === e.send_id) j++;
      const slice = events.slice(i, j);
      if (slice.length > 1) {
        out.push({
          kind: 'group',
          key: `g:${e.send_id}:${e.id}`,
          sendId: e.send_id,
          subject: e.send_subject,
          events: slice,
        });
      } else {
        out.push({ kind: 'single', event: e });
      }
      i = j;
    } else {
      out.push({ kind: 'single', event: e });
      i++;
    }
  }
  return out;
}

function ActivityGroupRow({
  group,
}: {
  group: Extract<EventGroup, { kind: 'group' }>;
}) {
  const [open, setOpen] = useState(false);
  const lead = group.events[0];
  const visual = EVENT_VISUAL[lead.event_type] ?? {
    icon: Send,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
  };
  const Icon = visual.icon;
  // De-dup event types into a stable, chronological-ish order
  // (oldest → newest = reverse of DESC).
  const types: string[] = [];
  for (let k = group.events.length - 1; k >= 0; k--) {
    const t = group.events[k].event_type;
    if (!types.includes(t)) types.push(t);
  }
  const fullTimestamp = new Date(lead.created_at).toLocaleString();

  return (
    <li className="text-sm">
      <div
        className="flex items-center gap-3 px-4 sm:px-5 py-3 cursor-pointer hover:bg-slate-50"
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${visual.bg}`}
        >
          <Icon size={14} className={visual.color} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
            <span className="text-slate-900 font-medium flex-shrink-0">
              {group.subject || 'Newsletter'}
            </span>
            <span className="text-slate-500 text-xs">
              {group.events.length} events
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {types.map((t) => {
              const v = EVENT_VISUAL[t];
              return (
                <span
                  key={t}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    v ? `${v.bg} ${v.color}` : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {EVENT_LABELS[t] || t}
                </span>
              );
            })}
          </div>
        </div>
        <span
          className="text-xs text-slate-500 flex-shrink-0"
          title={fullTimestamp}
        >
          {formatEventTimestamp(lead.created_at)}
        </span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-slate-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </div>
      {open && (
        <ul className="border-t border-slate-100 bg-slate-50/50 divide-y divide-slate-100">
          {group.events.map((e) => (
            <ActivityRow key={e.id} event={e} hideSubject />
          ))}
        </ul>
      )}
    </li>
  );
}

function ActivityRow({
  event,
  hideSubject = false,
}: {
  event: SubscriberEvent;
  hideSubject?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const visual = EVENT_VISUAL[event.event_type] ?? {
    icon: Mail,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
  };
  const Icon = visual.icon;
  const label = EVENT_LABELS[event.event_type] || event.event_type;
  const meta = event.metadata
    ? Object.entries(event.metadata).filter(
        ([, v]) => v !== null && v !== undefined && v !== '',
      )
    : [];
  const expandable = meta.length > 0;
  const fullTimestamp = new Date(event.created_at).toLocaleString();

  return (
    <li className="text-sm">
      <div
        className={`flex items-center gap-3 px-4 sm:px-5 py-3 ${
          expandable ? 'cursor-pointer hover:bg-slate-50' : ''
        }`}
        onClick={expandable ? () => setOpen((o) => !o) : undefined}
      >
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${visual.bg}`}
        >
          <Icon size={14} className={visual.color} />
        </span>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
          <span className="text-slate-900 font-medium flex-shrink-0">
            {label}
          </span>
          {event.send_subject && !hideSubject && (
            <span className="text-slate-500 truncate min-w-0">
              {event.send_subject}
            </span>
          )}
        </div>
        <span
          className="text-xs text-slate-500 flex-shrink-0"
          title={fullTimestamp}
        >
          {formatEventTimestamp(event.created_at)}
        </span>
        {expandable && (
          <ChevronDown
            size={16}
            className={`flex-shrink-0 text-slate-400 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>
      {expandable && open && (
        <div className="px-4 sm:px-5 pb-3 pl-[3.75rem] sm:pl-[4.25rem] space-y-1 text-xs">
          {meta.map(([k, v]) => (
            <div key={k} className="flex flex-col sm:flex-row sm:gap-3">
              <span className="text-slate-500 sm:w-36 sm:flex-shrink-0">
                {METADATA_LABELS[k] ?? k}
              </span>
              <span className="text-slate-700 font-mono break-all min-w-0">
                {k === 'url' && typeof v === 'string' ? (
                  <a
                    href={v}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline"
                  >
                    {v}
                  </a>
                ) : (
                  String(v)
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
